import { FlowType, Step as ModelStep } from "@redotech/redo-model/return-flow";
import { ActionType } from "@redotech/redo-model/return-flow/action";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import StarIcon from "@redotech/redo-web/icon-old/star.svg";
import { produce } from "immer";
import * as React from "react";
import { Dispatch, ReactElement, SetStateAction } from "react";
import {
  StepDownstream,
  StepId,
  StepType,
  StepTypeDetailsProps,
} from "../step";
import { ADD_TAG, AddTagState } from "./add-tag";
import { ASSIGN, AssignState } from "./assign";
import { AUTO_REPLY, AutoReplyState } from "./auto-reply";
import { SET_STATUS, SetStatusState } from "./set-status";

export interface RuleTypeDetailsProps<T> {
  state: T;
  setState: Dispatch<SetStateAction<T>>;
  disabled?: boolean;
  flowType: FlowType;
}

export interface ActionTypeSetup<T, M> {
  readonly empty: T;
  name: string;
  Details(props: RuleTypeDetailsProps<T>): ReactElement | null;
  description(state: T): string;
  valid(state: T): boolean;
  fromModel(model: M): T;
  toModel(state: T): M;
}

export interface MultipleActionsState {
  customTitle?: string;
  actions: ActionState[];
  next: StepId | undefined;
}

export interface ActionState {
  actionTypeSetup: ActionTypeSetup<any, any> | undefined;
  actionState?: any;
  customTitle?: string;
}

export interface DetailsComponentsForActions {
  SetStatusDetails: React.ComponentType<RuleTypeDetailsProps<SetStatusState>>;
  AddTagDetails: React.ComponentType<RuleTypeDetailsProps<AddTagState>>;
  AutoReplyDetails: React.ComponentType<RuleTypeDetailsProps<AutoReplyState>>;
  AssignDetails: React.ComponentType<RuleTypeDetailsProps<AssignState>>;
}

export const MULTIPLE_ACTIONS = (
  Details: React.ComponentType<StepTypeDetailsProps<MultipleActionsState>>,
  DetailsForActions: DetailsComponentsForActions,
): StepType<
  MultipleActionsState,
  { actions: ModelStep.Action[]; next?: number }
> => ({
  Details(props: StepTypeDetailsProps<MultipleActionsState>) {
    return React.createElement(Details, props);
  },
  customTitle(state) {
    return state.customTitle;
  },
  Icon: StarIcon,
  description(state) {
    return state.actions
      .map((action) => {
        return action.actionTypeSetup?.name || "One or more actions";
      })
      .join(", ");
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next, label: "Next" });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { actions: [], next: undefined },
  fromModel(model, id) {
    return {
      actions: model.actions.map<ActionState>((act) => {
        switch (act.action.type) {
          case ActionType.SetStatus: {
            return {
              customTitle: act.customTitle,
              actionTypeSetup: SET_STATUS(DetailsForActions.SetStatusDetails),
              actionState: SET_STATUS(
                DetailsForActions.SetStatusDetails,
              ).fromModel(act.action),
            };
          }
          case ActionType.AutoReply: {
            return {
              customTitle: act.customTitle,
              actionTypeSetup: AUTO_REPLY(DetailsForActions.AutoReplyDetails),
              actionState: AUTO_REPLY(
                DetailsForActions.AutoReplyDetails,
              ).fromModel(act.action),
            };
          }
          case ActionType.Assign: {
            return {
              customTitle: act.customTitle,
              actionTypeSetup: ASSIGN(DetailsForActions.AssignDetails),
              actionState: ASSIGN(DetailsForActions.AssignDetails).fromModel(
                act.action,
              ),
            };
          }
          default: {
            return {
              customTitle: act.customTitle,
              actionTypeSetup: ADD_TAG(DetailsForActions.AddTagDetails),
              actionState: ADD_TAG(DetailsForActions.AddTagDetails).fromModel(
                act.action,
              ),
            };
          }
        }
      }),
      next: model.next?.toString(),
    };
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  title: "Actions",
  toModel(state, id) {
    return {
      type: ModelStep.MULTIPLE_ACTIONS,
      actions: state.actions.map((act) => {
        return {
          type: ModelStep.ACTION,
          action: act.actionTypeSetup!.toModel(act.actionState),
        };
      }),
      next: state.next ? id(state.next) : undefined,
    };
  },
  valid(state) {
    return state.actions.length > 0;
  },
});
